import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, X, RefreshCw } from 'lucide-react';

/**
 * ErrorDisplay - Maps numeric backend error codes to user-friendly messages
 *
 * Handles FastAPI detail wrapper and provides actionable retry buttons
 * All 16 error codes from backend/python/d4rt-service/logger_config.py
 */

// Map numeric error codes to user-friendly messages with actions
const ERROR_MESSAGES = {
  // Video Processing (1000-1099)
  1000: {
    message: "We couldn't load the video file. It may have been moved or deleted.",
    action: "Retry Download",
    tooltip: "Videos must be in the SLAM cloud bucket (gs://slam-video-analysis/...)"
  },
  1001: {
    message: "The video file couldn't be opened. It may be damaged or unsupported.",
    action: "Choose Different Video",
    tooltip: "Supported formats: MP4, MOV, AVI"
  },
  1002: {
    message: "The frame range you selected is invalid. Please check the frame numbers.",
    action: "Adjust Frame Range",
    tooltip: "Start frame must be less than end frame, and both must be within video bounds"
  },
  1003: {
    message: "The frame index is out of bounds. The video doesn't have that many frames.",
    action: "Go to Valid Frame",
    tooltip: "Check the total frame count in the timeline"
  },

  // GCS Errors (1100-1199)
  1100: {
    message: "We couldn't access Google Cloud Storage. Check your permissions.",
    action: "Check Permissions",
    tooltip: "Ensure your service account has Storage Object Viewer role"
  },
  1101: {
    message: "The storage bucket was not found. It may have been deleted.",
    action: "Browse Available Buckets",
    tooltip: "Default bucket: gs://slam-video-analysis"
  },
  1102: {
    message: "The video file was not found in storage. It may have been moved or deleted.",
    action: "Browse Videos",
    tooltip: "Select a video from the available library"
  },

  // Gemini API Errors (1200-1299)
  1200: {
    message: "AI analysis service is temporarily unavailable. Please try again.",
    action: "Retry Analysis",
    tooltip: "If this persists, try a shorter video clip"
  },
  1201: {
    message: "AI analysis limit reached. Please try again in a few minutes.",
    action: "Retry in 60s",
    tooltip: "Analysis quota resets every few minutes",
    countdown: true
  },
  1202: {
    message: "The AI returned invalid data. This is unusual - please retry.",
    action: "Retry Analysis",
    tooltip: "If this persists, contact support with the error code"
  },

  // Analysis Errors (1300-1399)
  1300: {
    message: "No player movements detected in this frame range. Try a different moment.",
    action: "Jump Forward 30 Frames",
    tooltip: "Position the timeline where the player is clearly visible"
  },
  1301: {
    message: "Couldn't find the object in these frames. Try clicking closer to it.",
    action: "Try Again",
    tooltip: "Click directly on the ball/object at its clearest moment"
  },

  // Validation Errors (1400-1499)
  1400: {
    message: "The video ID format is invalid. Please select a video from the library.",
    action: "Go to Video Library",
    tooltip: "Video IDs must start with gs://"
  },
  1401: {
    message: "Required information is missing. Please check your input.",
    action: "Review Input",
    tooltip: "All fields must be filled correctly"
  },
  1402: {
    message: "An internal error occurred. Our team has been notified.",
    action: "Retry",
    tooltip: "If this persists, please contact support"
  },

  // Catch-all
  9999: {
    message: "Something went wrong on our end. Our team has been notified.",
    action: "Retry",
    link: "Report Issue"
  }
};

const ErrorDisplay = ({ error, onDismiss, onRetry }) => {
  const [showContext, setShowContext] = useState(false);
  const [countdown, setCountdown] = useState(60);

  // Extract error code (handle FastAPI detail wrapper)
  const errorCode = error?.detail?.error_code || error?.error_code || error?.code;
  const errorMessage = error?.detail?.error_message || error?.error_message || error?.message;
  const errorContext = error?.detail?.context || error?.context || {};

  const errorData = ERROR_MESSAGES[errorCode] || ERROR_MESSAGES[9999];

  // Handle countdown for rate limit errors
  React.useEffect(() => {
    if (errorData.countdown && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (errorData.countdown && countdown === 0 && onRetry) {
      onRetry(); // Auto-retry when countdown finishes
    }
  }, [countdown, errorData.countdown, onRetry]);

  if (!error) return null;

  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2 flex-1">
          <AlertCircle className="text-red-400 mt-0.5 flex-shrink-0" size={18} />

          <div className="flex-1">
            {/* Error code badge */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-red-400 font-semibold text-sm">
                Error {errorCode}
              </span>
              <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-300 font-mono">
                {errorCode}
              </span>
            </div>

            {/* User-friendly message */}
            <p className="text-zinc-300 text-sm leading-relaxed">
              {errorData.message}
            </p>

            {/* Original backend error message (if different) */}
            {errorMessage && errorMessage !== errorData.message && (
              <p className="text-zinc-400 text-xs italic mt-1">
                Technical: {errorMessage}
              </p>
            )}

            {/* Tooltip hint */}
            {errorData.tooltip && (
              <p className="text-zinc-500 text-xs mt-2">
                💡 {errorData.tooltip}
              </p>
            )}
          </div>
        </div>

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className="p-1 rounded hover:bg-red-500/20 transition-colors flex-shrink-0"
          aria-label="Dismiss error"
        >
          <X className="text-red-400" size={16} />
        </button>
      </div>

      {/* Action button */}
      {onRetry && errorData.action && (
        <div className="mt-3">
          <button
            onClick={onRetry}
            disabled={errorData.countdown && countdown > 0}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-red-200 border border-red-500/50 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw size={14} className={errorData.countdown && countdown > 0 ? '' : ''} />
            {errorData.countdown && countdown > 0
              ? `Retry in ${countdown}s`
              : errorData.action
            }
          </button>
        </div>
      )}

      {/* Context details (collapsible) */}
      {errorContext && Object.keys(errorContext).length > 0 && (
        <div className="mt-3 border-t border-red-500/20 pt-3">
          <button
            onClick={() => setShowContext(!showContext)}
            className="flex items-center gap-1 text-zinc-400 hover:text-zinc-300 text-xs transition-colors"
          >
            {showContext ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
            Technical details
          </button>

          {showContext && (
            <pre className="mt-2 p-2 bg-black/30 rounded text-xs text-zinc-400 overflow-auto max-h-32 font-mono">
              {JSON.stringify(errorContext, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default ErrorDisplay;
